// Validation utility for Monthly Inspection Reports
// Matches Laravel staging validation behavior EXACTLY

import { usesNewMeters } from "./meterCutover";

export const validateMonthlyInspectionForm = (formData, options = {}) => {
  const errors = [];
  // `meters` is the property meter register shown on the form (Oct 2026+
  // reports). Needed here so an error can name the meter the way the
  // inspector sees it -- type and serial -- rather than a bare id.
  // `requiredMeterIds` narrows which of `meters` must carry a reading.
  // Creating a report: every meter on the register (the default). Editing
  // one: only the meters it already carries -- a meter added to the register
  // afterwards appears on the form but cannot be required retrospectively.
  const {
    isMetropolitanHousing = false,
    meters = [],
    requiredMeterIds = null,
    // Set when the property is marked as genuinely having no meters, which
    // is the one legitimate reason for an empty register.
    noMetersConfirmed = false,
  } = options;

  // Laravel Base Rules (CreateRequest.php)
  // Required fields validation - matching Laravel exactly
  const requiredFields = [
    { field: "status_id", label: "Status", type: "boolean" }, // Laravel: required|boolean
    { field: "brief_overview", label: "Brief Overview" }, // Laravel: required
    { field: "month", label: "Month", maxLength: 255 }, // Laravel: required|max:255
    { field: "year", label: "Year", maxLength: 255 }, // Laravel: required|max:255
    { field: "client", label: "Client", maxLength: 255 }, // Laravel: required|max:255
    { field: "account_manager", label: "Account Manager", maxLength: 255 }, // Laravel: required|max:255
    { field: "account_manager_email", label: "Account Manager Email", maxLength: 255 }, // Laravel: required|email|max:255
    { field: "client_contact", label: "Contact Person", maxLength: 255 }, // Laravel: required|max:255
    { field: "client_contact_short", label: "Contact First Name", maxLength: 255 }, // Laravel: required|max:255
    { field: "savings_made", label: "Savings Made", type: "numeric" }, // Laravel: required|numeric
  ];

  // Laravel Standard Type Rules (Standard.php)
  const standardTypeRequiredFields = [
    { field: "type_id", label: "Type ID" }, // Laravel: required|in:typeId
    { field: "property_id", label: "Property ID" }, // Laravel: required|exists:properties,id
    { field: "inspection_date", label: "Inspection Date", format: "d/m/Y" }, // Laravel: required|date_format:d/m/Y
    { field: "lease_start_date", label: "Lease Start Date", format: "d/m/Y" }, // Laravel: required|date_format:d/m/Y
    { field: "property_id_name", label: "Property ID", maxLength: 255 }, // Laravel: required|max:255
    { field: "address_1", label: "Address 1", maxLength: 255 }, // Laravel: required|max:255
    { field: "postcode", label: "Postcode", maxLength: 255 }, // Laravel: required|max:255
    { field: "town", label: "Town", maxLength: 255 }, // Laravel: required|max:255
    { field: "guardians_number", label: "Number of Guardians in Occupation", type: "integer" }, // Laravel: required|integer
    { field: "months_under_management", label: "Amount of Months Since Lease Start Date", type: "integer" }, // Laravel: required|integer
  ];

  // Check base required fields (matching Laravel CreateRequest.php)
  requiredFields.forEach(({ field, label, type, maxLength }) => {
    const value = formData[field];
    
    // Check if field is required and empty
    if (value === null || value === undefined || value.toString().trim() === "") {
      errors.push(`${label} is required`);
      return;
    }
    
    // Check max length if specified (Laravel: max:255)
    if (maxLength && value.toString().length > maxLength) {
      errors.push(`${label} must not exceed ${maxLength} characters`);
    }
    
    // Check type if specified
    // Note: HTML select elements return string values, so we need to accept both "0"/"1" and 0/1
    if (type === "boolean" && typeof value !== "boolean" && value !== 0 && value !== 1 && value !== "0" && value !== "1") {
      errors.push(`${label} must be a boolean value (0 or 1)`);
    }
    
    if (type === "numeric" && isNaN(parseFloat(value))) {
      errors.push(`${label} must be a valid number`);
    }
  });

  // Check standard type required fields (matching Laravel Standard.php)
  standardTypeRequiredFields.forEach(({ field, label, type, maxLength, format }) => {
    const value = formData[field];
    
    // Check if field is required and empty
    if (value === null || value === undefined || value.toString().trim() === "") {
      errors.push(`${label} is required`);
      return;
    }
    
    // Check max length if specified (Laravel: max:255)
    if (maxLength && value.toString().length > maxLength) {
      errors.push(`${label} must not exceed ${maxLength} characters`);
    }
    
    // Check date format if specified (Laravel: date_format:d/m/Y)
    if (format === "d/m/Y" && value) {
      if (!isValidDateFormat(value, format)) {
        errors.push(`${label} must be in format ${format} (e.g., 11/12/2025)`);
      }
    }
    
    // Check type if specified
    if (type === "integer" && (!Number.isInteger(parseInt(value)) || isNaN(parseInt(value)))) {
      errors.push(`${label} must be a valid integer`);
    }
  });

  // Validate email format (Laravel: required|email|max:255)
  if (formData.account_manager_email) {
    if (!isValidEmail(formData.account_manager_email)) {
      errors.push("Account Manager Email must be a valid email address");
    }
    if (formData.account_manager_email.length > 255) {
      errors.push("Account Manager Email must not exceed 255 characters");
    }
  }

  // Validate block_id (Laravel: integer, can be 0 for "Other")
  if (formData.block_id !== null && formData.block_id !== undefined && formData.block_id !== "") {
    if (!Number.isInteger(parseInt(formData.block_id)) && formData.block_id !== 0) {
      errors.push("Block ID must be a valid integer");
    }
  }

  // Validate signature (most important validation like Laravel)
  // MTVH properties do NOT require signature (matching Laravel behavior)
  if (!isMetropolitanHousing) {
    if (
      !formData.e_sign ||
      formData.e_sign.trim() === "" ||
      formData.e_sign === "null"
    ) {
      errors.push("You have to sign! The field is blank!");
    }
  }

  // NOTE: legacy reading_fields validation lives further down, in one place.
  // A second copy used to sit here and reported every fault twice.

  // Validate photo fields
  if (formData.photo_fields && formData.photo_fields.length > 0) {
    formData.photo_fields.forEach((photo, index) => {
      const photoName =
        formData.photo_fields_names[index] || `Photo ${index + 1}`;
      if (!photo || photo.toString().trim() === "") {
        errors.push(`${photoName} is required`);
      }
    });
  }

  // Validate meter photo fields
  if (formData.meter_photo_fields && formData.meter_photo_fields.length > 0) {
    formData.meter_photo_fields.forEach((photo, index) => {
      const photoName =
        formData.meter_photo_fields_names[index] || `Meter Photo ${index + 1}`;
      if (!photo || photo.toString().trim() === "") {
        errors.push(`${photoName} is required`);
      }
    });
  }

  // Meter readings, Oct 2026 onwards: one reading per meter on the property
  // register. Type, serial and location are validated on the property page,
  // not here -- the inspector only supplies the reading.
  if (usesNewMeters(formData)) {
    const readings = formData.meter_readings || {};

    if (meters.length === 0 && !noMetersConfirmed) {
      errors.push(
        "No meters are set up for this property. Add them on the property page before completing this inspection."
      );
    }

    const mustHaveReading =
      requiredMeterIds === null
        ? meters.map((meter) => meter.id)
        : requiredMeterIds;

    meters.forEach((meter) => {
      const label = [meter.meter_type, meter.serial_number]
        .filter(Boolean)
        .join(" ");
      const reading = readings[meter.id];
      const isBlank = !reading || reading.toString().trim() === "";

      if (isBlank) {
        // Anything entered is still length-checked below; only the
        // requirement to enter something at all is conditional.
        if (mustHaveReading.includes(meter.id)) {
          errors.push(`${label} reading is required`);
        }
      } else if (reading.toString().length > 255) {
        errors.push(`${label} reading must not exceed 255 characters`);
      }
    });
  }

  // Validate reading fields for legacy reports only (Sep 2026 and earlier).
  // Laravel: reading_fields.*, reading_fields_names.*, reading_fields_serials.* => required|max:255
  if (
    !usesNewMeters(formData) &&
    formData.reading_fields &&
    formData.reading_fields.length > 0
  ) {
    formData.reading_fields.forEach((reading, index) => {
      const meterName = formData.reading_fields_names[index] || `Meter ${index + 1}`;
      
      // Validate reading field (Laravel: required|max:255)
      if (!reading || reading.toString().trim() === "") {
        errors.push(`${meterName} reading is required`);
      } else if (reading.toString().length > 255) {
        errors.push(`${meterName} reading must not exceed 255 characters`);
      }
      
      // Validate reading name (Laravel: required|max:255)
      const readingName = formData.reading_fields_names[index];
      if (!readingName || readingName.toString().trim() === "") {
        errors.push(`${meterName} name is required`);
      } else if (readingName.toString().length > 255) {
        errors.push(`${meterName} name must not exceed 255 characters`);
      }
      
      // Validate reading serial (Laravel: required|max:255)
      const readingSerial = formData.reading_fields_serials[index];
      if (!readingSerial || readingSerial.toString().trim() === "") {
        errors.push(`${meterName} serial number is required`);
      } else if (readingSerial.toString().length > 255) {
        errors.push(`${meterName} serial number must not exceed 255 characters`);
      }
    });
  }

  // Validate optional fields with max length (Laravel: max:255)
  const optionalMaxLengthFields = [
    { field: "address_2", label: "Address 2" },
    { field: "address_3", label: "Address 3" },
  ];
  
  optionalMaxLengthFields.forEach(({ field, label }) => {
    if (formData[field] && formData[field].toString().length > 255) {
      errors.push(`${label} must not exceed 255 characters`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Helper functions
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const isValidDate = (dateString) => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

// Validate date format (Laravel: date_format:d/m/Y)
const isValidDateFormat = (dateString, format) => {
  if (format === "d/m/Y") {
    // Match Laravel date format: d/m/Y (e.g., 11/12/2025)
    const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = dateString.match(dateRegex);
    
    if (!match) {
      return false;
    }
    
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    
    // Validate date values
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > 2100) {
      return false;
    }
    
    // Check if date is valid (e.g., not 31/02/2025)
    const date = new Date(year, month - 1, day);
    return date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year;
  }
  
  return isValidDate(dateString);
};

// Show validation errors as alerts (matching Laravel behavior)
export const showValidationErrors = (errors) => {
  if (errors.length > 0) {
    // Show the first error as an alert (like Laravel does)
    alert(errors[0]);
    return false;
  }
  return true;
};

// Validate and show errors
export const validateAndShowErrors = (formData, options = {}) => {
  const validation = validateMonthlyInspectionForm(formData, options);
  return showValidationErrors(validation.errors);
};
